import axios from "axios";

const WIKIDATA_ENDPOINT = "https://query.wikidata.org/sparql";

/**
 * Fetch FULL cast using Wikidata SPARQL, then fill missing photos via Wikipedia.
 */
export async function fetchCast(movie) {
  if (!movie || !movie.imdbID) return [];

  try {
    const query = `
      SELECT ?actorLabel ?characterLabel ?image ?article WHERE {
        ?film wdt:P345 "${movie.imdbID}" .
        ?film wdt:P161 ?actor .
        OPTIONAL { ?actor wdt:P18 ?image . }
        OPTIONAL {
          ?article schema:about ?actor ;
                   schema:isPartOf <https://en.wikipedia.org/> .
        }
        OPTIONAL {
          ?film p:P161 ?castStatement .
          ?castStatement ps:P161 ?actor .
          ?castStatement pq:P453 ?character .
        }
        SERVICE wikibase:label { bd:serviceParam wikibase:language "en" . }
      }
    `;

    const res = await axios.get(WIKIDATA_ENDPOINT, {
      params: { query, format: "json" },
      headers: { Accept: "application/sparql-results+json" },
    });

    const results = res.data.results.bindings;
    if (!results || results.length === 0) return fallbackCast(movie);

    // Deduplicate by actor name
    const seen = new Set();
    const cast = [];

    results.forEach((r) => {
      const name = r.actorLabel ? r.actorLabel.value : null;
      if (!name || seen.has(name)) return;
      seen.add(name);

      let photoUrl = null;
      if (r.image) {
        // Use Wikimedia thumbnail API for reliable image loading
        const filename = decodeURIComponent(
          r.image.value.split("/").pop()
        );
        photoUrl = getWikimediaThumb(filename, 400);
      }

      cast.push({
        id: cast.length,
        name,
        character: r.characterLabel ? r.characterLabel.value : "",
        profileUrl: photoUrl,
        wikiUrl: r.article ? r.article.value : null,
        order: cast.length,
      });
    });

    if (cast.length === 0) return fallbackCast(movie);

    // Fill missing photos from Wikipedia in parallel
    const missingPhotoIndexes = cast
      .map((m, i) => (m.profileUrl ? null : i))
      .filter((i) => i !== null);

    if (missingPhotoIndexes.length > 0) {
      const photoPromises = missingPhotoIndexes.map((i) =>
        fetchWikiPhoto(cast[i].name)
      );
      const photos = await Promise.all(photoPromises);
      missingPhotoIndexes.forEach((idx, j) => {
        cast[idx].profileUrl = photos[j];
      });
    }

    return cast;
  } catch (err) {
    console.log("Wikidata fetch failed:", err.message);
    return fallbackCast(movie);
  }
}

/**
 * Convert a Wikimedia Commons filename to a reliable thumbnail URL.
 * Uses the Special:FilePath endpoint with width parameter.
 */
function getWikimediaThumb(filename, width) {
  const encoded = encodeURIComponent(filename.replace(/ /g, "_"));
  return `https://commons.wikimedia.org/w/thumb.php?f=${encoded}&w=${width}`;
}

/**
 * Fallback: use OMDB Actors field + Wikipedia photos
 */
async function fallbackCast(movie) {
  if (!movie.Actors || movie.Actors === "N/A") return [];

  const names = movie.Actors.split(", ").filter(Boolean);
  const photoPromises = names.map(fetchWikiPhoto);
  const photos = await Promise.all(photoPromises);

  return names.map((name, i) => ({
    id: i,
    name,
    character: "",
    profileUrl: photos[i],
    wikiUrl: null,
    order: i,
  }));
}

/**
 * Fetch actor photo from Wikipedia REST API.
 */
async function fetchWikiPhoto(name) {
  try {
    const encoded = encodeURIComponent(name.replace(/ /g, "_"));
    const res = await axios.get(
      `https://en.wikipedia.org/api/rest_v1/page/summary/${encoded}`
    );
    if (res.data.originalimage && res.data.originalimage.source) {
      return res.data.originalimage.source;
    }
    if (res.data.thumbnail && res.data.thumbnail.source) {
      // Request larger version
      return res.data.thumbnail.source.replace(/\/\d+px-/, "/400px-");
    }
    return null;
  } catch {
    return null;
  }
}
